// ─────────────────────────────────────────────────────────────────────────────
// boardEvents — a board write reaches the page as an event, not after a poll.
//
// Owner ruling 2026-09-15: "THE SCREEN MUST KEEP UP." A panel published in
// milliseconds sat unseen for up to POLL_MS. The server emits
// `domain_board_changed` after every panel publish (Delta's through
// core/board_producer, Buck's through server/growth_panels — the same
// listeners): {domain, root, company_id, panels, stamp}. IDS AND NAMES, NEVER
// CONTENT — the page re-reads the board route, which stays the one place a board
// is served and authorised.
//
// THE SHARED SOCKET, NOT A SECOND ONE. `getRexySocket()` is the default-namespace
// connection the vitals and telemetry cards already hold, and the emit is a
// default-namespace broadcast.
//
// NO TOKEN, NO SOCKET. The server rejects a connect without one, so opening it
// would only produce a refused handshake and a retry loop; the 30 s poll still
// runs underneath either way.
// ─────────────────────────────────────────────────────────────────────────────

import { getRexySocket } from '../../hooks/useRexySocket';

export interface BoardChanged {
  domain: string;
  root: string | null;
  companyId: string | null;
  panels: string[];
  stamp: number | null;
  /** FALSE only when the producer says its panels aggregate every company (Buck's).
   *  Anything else — absent, null — is read as company-scoped (Delta's roots). */
  companyScoped: boolean;
}

/** The wire payload → the event, or null when it is not one. Never throws. */
export function readBoardChanged(d: any): BoardChanged | null {
  if (!d || typeof d !== 'object' || Array.isArray(d)) return null;
  const own = (k: string) => Object.prototype.hasOwnProperty.call(d, k) ? d[k] : undefined;
  const domain = own('domain');
  if (typeof domain !== 'string' || !domain.trim()) return null;
  const cid = own('company_id');
  const panels = own('panels');
  const stamp = own('stamp');
  const root = own('root');
  return {
    domain: domain.trim(),
    root: typeof root === 'string' ? root : null,
    companyId: typeof cid === 'string' && cid.trim() ? cid.trim() : null,
    panels: Array.isArray(panels) ? panels.filter((p: unknown): p is string => typeof p === 'string') : [],
    stamp: typeof stamp === 'number' && Number.isFinite(stamp) ? stamp : null,
    companyScoped: own('company_scoped') !== false,
  };
}

/**
 * SHOULD A SURFACE HOLDING `domain`, READ FOR `company`, RE-READ ON `event`?
 *
 * Pure, so the rule is testable without a socket.
 *  • another domain's board moved → no.
 *  • a per-company read (`company` set), a COMPANY-SCOPED event, and it names
 *    ANOTHER company → no.
 *    The owner has moved away from that company; re-reading would be a wasted
 *    request at best, and it is the same rule the superseded-answer drop applies.
 *  • a whole-board read (`company` null), an event naming no company, the same
 *    company, or panels that aggregate EVERY company (Buck's — a write for any
 *    company changes what this read returns) → yes.
 */
export function boardEventApplies(event: BoardChanged | null, domain: string,
                                  company: string | null): boolean {
  if (!event) return false;
  if (event.domain.toUpperCase() !== String(domain || '').toUpperCase()) return false;
  if (company && event.companyScoped && event.companyId && event.companyId !== company) return false;
  return true;
}

/** Subscribe; returns the unsubscribe. A no-op (and a no-op unsubscribe) when there
 *  is no token or no socket can be made. */
export function subscribeBoardChanged(cb: (event: BoardChanged) => void): () => void {
  try {
    if (typeof window === 'undefined' || !(window as any).__rexyToken) return () => {};
    const s = getRexySocket();
    const handler = (d: any) => {
      const ev = readBoardChanged(d);
      if (ev) cb(ev);
    };
    s.on('domain_board_changed', handler);
    return () => { s.off('domain_board_changed', handler); };
  } catch {
    return () => {};
  }
}
